import React from 'react';
import Tooltip from '@/components/atoms/Tooltip';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { FaUndo } from 'react-icons/fa';
import { type ColumnDef } from '@/components/organism/Table';
import { Cell, PillCell, AppButton } from '@/components/atoms';
import type { Operator } from '@/store/types';
import { OPERATOR_PROPS } from '@/constant/operator/operator.constant';
import { tid } from '@/utils/testId';
import { OPERATOR_TEST_IDS as TID } from '@/constant/testIds/operator.testIds';
import { getDateTime, getDateTimeAgo } from '@/utils/dateTime';

/* ── Column definitions ───────────────────────────────────────────────────── */
export function buildColumns(
  isCopyRef: React.MutableRefObject<string>,
  onCopyClick: (key: string) => void,
  onEdit: (item: Operator) => void,
  onDelete: (item: Operator) => void,
  userHasWriteAccess: boolean,
  isAdmin: boolean = false,
  onActivate?: (item: Operator) => void,
): ColumnDef<Operator>[] {
  const makeCopy = (copyKey: string) => ({
    copyKey,
    get isCopy() {
      return isCopyRef.current;
    },
    onCopyClick,
  });

  return [
    {
      accessor: 'sourceId',
      header: OPERATOR_PROPS.COLUMNS.OPERATOR_ID,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_SOURCE_ID(`${idx}`))}
          value={item.sourceId}
          tooltip
          variant="primary"
          copy={makeCopy(`sid-${item.operatorId}`)}
        />
      ),
    },
    {
      accessor: 'name',
      header: OPERATOR_PROPS.COLUMNS.NAME,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_NAME(`${idx}`))}
          value={item.name}
          className="font-medium"
        />
      ),
    },
    {
      accessor: 'providerId',
      header: OPERATOR_PROPS.COLUMNS.PROVIDER,
      sorting: true,
      render: (item, idx) =>
        item?.provider?.server ? (
          <PillCell
            testId={tid(TID.COL_PROVIDER(`${idx}`))}
            value={item?.provider?.server}
            preset="subapp"
            dot
            className="uppercase tracking-widest border font-bold cursor-pointer"
            tooltipTitle="Provider Profile"
            tooltipValue={item?.provider?.profile}
            tooltip
          />
        ) : (
          <span className="text-gray-400 dark:text-gray-600 text-[11px]">
            —
          </span>
        )
    },
    {
      accessor: 'subapplication',
      header: OPERATOR_PROPS.COLUMNS.SUB_APPLICATION,
      render: (item, idx) => {
        const name = item.subapplication?.name;
        if (!name)
          return (
            <span className="text-gray-400 dark:text-gray-600 text-[11px]">
              —
            </span>
          );
        return (
          <PillCell
            testId={tid(TID.COL_SUBAPP(`${idx}`))}
            value={name.toUpperCase()}
            preset="subapp"
            dot
            className="uppercase tracking-widest border font-bold cursor-pointer"
          />
        );
      },
    },
    {
      accessor: 'operatorId',
      header: OPERATOR_PROPS.COLUMNS.OPERATOR_UUID,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_UUID(`${idx}`))}
          value={item.operatorId}
          tooltip
          variant="mono"
          size="text-[11px]"
          copy={makeCopy(`uuid-${item.operatorId}`)}
        />
      ),
    },
    {
      accessor: 'updatedOn',
      header: OPERATOR_PROPS.COLUMNS.UPDATED_ON,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_UPDATED_ON(`${idx}`))}
          value={item.updatedOn}
          variant="muted"
          tooltip={getDateTime(item.updatedOn)}
          tooltipPlacement="top"
        >
          {item.updatedOn ? getDateTimeAgo(item.updatedOn) : '—'}
        </Cell>
      ),
    },
    {
      accessor: 'createdOn',
      header: OPERATOR_PROPS.COLUMNS.CREATED_ON,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_CREATED_ON(`${idx}`))}
          value={item.createdOn}
          format="datetime"
          variant="muted"
        />
      ),
    },
    {
      accessor: 'action',
      header: OPERATOR_PROPS.COLUMNS.ACTION,
      render: (item, idx) => (
        <div className="flex items-center gap-1.5">
          {isAdmin && item.deletedOn ? (
            <Tooltip content="Reactivate Operator" placement="top">
              <span>
                <AppButton
                  data-testid={tid(TID.COL_REACTIVATE_BTN(`${idx}`))}
                  variant="success"
                  shape="circle"
                  tint
                  size="xs"
                  hoverBg={false}
                  icon={<FaUndo className="h-3.5 w-3.5 rotate-[-90deg]" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onActivate?.(item);
                  }}
                />
              </span>
            </Tooltip>
          ) : (
            userHasWriteAccess && (
              <>
                <Tooltip content="Edit Operator" placement="top">
                  <AppButton
                    data-testid={tid(TID.COL_EDIT_BTN(`${idx}`))}
                    variant="info"
                    shape="circle"
                    size="xs"
                    hoverBg={false}
                    icon={<FiEdit2 className="h-3.5 w-3.5" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(item);
                    }}
                  />
                </Tooltip>
                {isAdmin && (
                  <AppButton
                    data-testid={tid(TID.COL_DELETE_BTN(`${idx}`))}
                    variant="danger"
                    tint
                    shape="circle"
                    size="xs"
                    hoverBg={false}
                    icon={<FiTrash2 className="h-3.5 w-3.5" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(item);
                    }}
                  />
                )}
              </>
            )
          )}
        </div>
      ),
    },
  ];
}
